import { DomainError } from '../errors.js';
import { compareLocalDate, type LocalDate, parseLocalDate } from '../local-date.js';
import type { DefectKind, DefectSeverity, DefectStatus } from './defects.js';

/**
 * Arıza bildirimi ve giderilmesi, DM-17 (ADR 0060): PMS arızasının ortağın formundan gelen
 * alanları. Arıza PMS kaydı olarak kalır; bu modül yalnız ek alanların kurallarıdır. Saf modül.
 */

/** Departman (DM-17 "Department"; HSE listesinin teknik kısmı). */
export const DEFECT_DEPARTMENTS = [
  'deck',
  'engine',
  'electrical',
  'navigation',
  'safety',
  'accommodation',
  'galley',
  'other',
] as const;
export type DefectDepartment = (typeof DEFECT_DEPARTMENTS)[number];

/** Öncelik (DM-17 "Priority"; iş emri öncelikleriyle aynı). */
export const DEFECT_PRIORITIES = ['low', 'medium', 'high'] as const;
export type DefectPriority = (typeof DEFECT_PRIORITIES)[number];

/** Faaliyet satırının durumu (DM-17 "Corrective Actions"). */
export const DEFECT_ACTION_STATUSES = ['open', 'in_progress', 'completed', 'cancelled'] as const;
export type DefectActionStatus = (typeof DEFECT_ACTION_STATUSES)[number];

/**
 * LGS-17'nin dört ana aşaması (ADR 0096 §2; ADR 0060 §10'un yerine): Reported, Under Action,
 * Verification, Closed. Durumdan ve kapanıştan türer, saklanmaz; ayrıntılı basamaklar Workflow
 * sekmesindedir.
 */
export const DEFECT_STAGES = ['reported', 'under_action', 'verification', 'closed'] as const;
export type DefectStage = (typeof DEFECT_STAGES)[number];

/** Ofisin notu (ADR 0060 §6): Part C yönetim faaliyeti ya da yorum. */
export const DEFECT_OFFICE_NOTE_KINDS = ['management_action', 'comment'] as const;
export type DefectOfficeNoteKind = (typeof DEFECT_OFFICE_NOTE_KINDS)[number];

export const DEFECT_MAX_ACTIONS = 20;
export const DEFECT_SHORT_TEXT = 200;
export const DEFECT_ACTION_TEXT = 500;
export const DEFECT_LONG_TEXT = 2000;

export interface DefectAction {
  /** UUIDv7; sunucu verir. */
  id: string;
  description: string;
  responsible: string | null;
  targetOn: LocalDate | null;
  status: DefectActionStatus;
  completedOn: LocalDate | null;
  /** Kanıt: metin (fotoğraf, rapor, servis raporu no) ya da bağlı iş emri. */
  evidence: string | null;
  workOrderId: string | null;
  cancelReason: string | null;
}

export interface ProcedureReview {
  /** "Does this defect require any update to SMS documentation or procedures?" */
  required: boolean;
  procedure: string | null;
  improvement: string | null;
}

/** DM-17'nin `defects` kolonlarındaki ek alanları (ADR 0060 §1). */
export interface DefectReportFields {
  department: DefectDepartment | null;
  location: string | null;
  systemName: string | null;
  priority: DefectPriority | null;
  targetOn: LocalDate | null;
  captainActionOn: LocalDate | null;
  rectifiedOn: LocalDate | null;
  procedureReview: ProcedureReview | null;
  actions: DefectAction[];
  jobDefinitionId: string | null;
  dailyReportId: string | null;
  /** LGS-17 Part A "Action required to rectify defect" (ADR 0096 §3). */
  actionRequired: string | null;
  /** Kaynak HSEQ raporu (`source = 'hse_report'`; ADR 0096 §5). */
  hseReportId: string | null;
  /** Bakım sırasında açılan arızanın kaynak iş emri (`source = 'work_record'`; ADR 0096 §6). */
  sourceWorkOrderId: string | null;
}

export const EMPTY_DEFECT_REPORT: Readonly<DefectReportFields> = Object.freeze({
  department: null,
  location: null,
  systemName: null,
  priority: null,
  targetOn: null,
  captainActionOn: null,
  rectifiedOn: null,
  procedureReview: null,
  actions: [],
  jobDefinitionId: null,
  dailyReportId: null,
  actionRequired: null,
  hseReportId: null,
  sourceWorkOrderId: null,
});

const blank = (value: string | null | undefined) => !value?.trim();
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function text(value: unknown, max: number, field: string): void {
  if (value === null || value === undefined) return;
  if (typeof value !== 'string' || value.length > max) {
    throw new DomainError('INVALID_DEFECT', field);
  }
}

function oneOf(value: unknown, list: readonly string[], field: string): void {
  if (value === null || value === undefined) return;
  if (typeof value !== 'string' || !list.includes(value)) {
    throw new DomainError('INVALID_DEFECT', `${field}:${String(value)}`);
  }
}

function day(value: unknown, field: string): LocalDate | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') throw new DomainError('INVALID_DEFECT', field);
  parseLocalDate(value);
  return value;
}

/** Ciddiyetten öncelik: öncelik verilmeyen arıza (eski istemci, ölçümden açılan). */
export function defectPriorityFor(severity: DefectSeverity | string): DefectPriority {
  if (severity === 'critical') return 'high';
  if (severity === 'major') return 'medium';
  return 'low';
}

/**
 * DM-17 alanlarının kuralları (ADR 0060 §1, §2, §5): listeler (`INVALID_DEFECT`); ekipmansız arıza
 * sistem adı ister (`SYSTEM_REQUIRED`), breakdown ekipman ister (`EQUIPMENT_REQUIRED`); hedef gün
 * bildirimden önce olamaz (`TARGET_BEFORE_REPORTED`); eylem ve onarım günü bildirimden önce
 * (`DATE_BEFORE_REPORTED`) ve gemi gününden sonra (`FUTURE_DATE`) olamaz; prosedür gözden
 * geçirmede güncelleme gerekiyorsa ikisi zorunlu; faaliyet satırları (`validateDefectAction`).
 */
export function validateDefectReport(
  fields: DefectReportFields,
  context: {
    kind: DefectKind | string;
    equipmentId: string | null;
    /** Bildirim günü (gemi yerel). */
    localDate: LocalDate;
    /** Gemi sunucusunun gemi günü. */
    today: LocalDate;
  },
): void {
  oneOf(fields.department, DEFECT_DEPARTMENTS, 'department');
  oneOf(fields.priority, DEFECT_PRIORITIES, 'priority');
  text(fields.location, DEFECT_SHORT_TEXT, 'location');
  text(fields.systemName, DEFECT_SHORT_TEXT, 'systemName');
  text(fields.actionRequired, DEFECT_LONG_TEXT, 'actionRequired');
  if (context.equipmentId === null) {
    if (context.kind === 'breakdown') throw new DomainError('EQUIPMENT_REQUIRED');
    if (blank(fields.systemName)) throw new DomainError('SYSTEM_REQUIRED');
  }
  const target = day(fields.targetOn, 'targetOn');
  if (target !== null && compareLocalDate(target, context.localDate) < 0) {
    throw new DomainError('TARGET_BEFORE_REPORTED', target);
  }
  for (const [value, field] of [
    [fields.captainActionOn, 'captainActionOn'],
    [fields.rectifiedOn, 'rectifiedOn'],
  ] as const) {
    const at = day(value, field);
    if (at === null) continue;
    if (compareLocalDate(at, context.localDate) < 0) {
      throw new DomainError('DATE_BEFORE_REPORTED', field);
    }
    if (compareLocalDate(at, context.today) > 0) throw new DomainError('FUTURE_DATE', field);
  }
  const review = fields.procedureReview;
  if (review !== null) {
    if (typeof review !== 'object' || typeof review.required !== 'boolean') {
      throw new DomainError('INVALID_DEFECT', 'procedureReview');
    }
    text(review.procedure, DEFECT_SHORT_TEXT, 'procedureReview.procedure');
    text(review.improvement, DEFECT_LONG_TEXT, 'procedureReview.improvement');
    if (review.required && (blank(review.procedure) || blank(review.improvement))) {
      throw new DomainError('PROCEDURE_REVIEW_INCOMPLETE');
    }
  }
  for (const id of [
    fields.jobDefinitionId,
    fields.dailyReportId,
    fields.hseReportId,
    fields.sourceWorkOrderId,
  ]) {
    if (id !== null && (typeof id !== 'string' || !UUID.test(id))) {
      throw new DomainError('INVALID_DEFECT', 'link');
    }
  }
  if (!Array.isArray(fields.actions) || fields.actions.length > DEFECT_MAX_ACTIONS) {
    throw new DomainError('INVALID_DEFECT', 'actions');
  }
  const ids = new Set<string>();
  for (const item of fields.actions) {
    validateDefectAction(item, context.today);
    if (ids.has(item.id)) throw new DomainError('INVALID_DEFECT', `actions:${item.id}`);
    ids.add(item.id);
  }
}

/**
 * Faaliyet satırı (ADR 0060 §5): açıklama zorunlu; tamamlanan satır tamamlanma günü
 * (`COMPLETED_DATE_REQUIRED`, gelecekte değil) ve kanıt ister (`EVIDENCE_REQUIRED`: metin ya da iş
 * emri); iptal gerekçe ister (`CANCEL_REASON_REQUIRED`); tamamlanmamış satırda tamamlanma günü yok.
 */
export function validateDefectAction(item: DefectAction, today: LocalDate): void {
  if (!item || typeof item !== 'object' || typeof item.id !== 'string' || !UUID.test(item.id)) {
    throw new DomainError('INVALID_DEFECT', 'actions.id');
  }
  oneOf(item.status, DEFECT_ACTION_STATUSES, 'actions.status');
  if (item.status === undefined) throw new DomainError('INVALID_DEFECT', 'actions.status');
  if (typeof item.description !== 'string' || blank(item.description)) {
    throw new DomainError('ACTION_DESCRIPTION_REQUIRED');
  }
  text(item.description, DEFECT_ACTION_TEXT, 'actions.description');
  text(item.responsible, DEFECT_SHORT_TEXT, 'actions.responsible');
  text(item.evidence, DEFECT_ACTION_TEXT, 'actions.evidence');
  text(item.cancelReason, DEFECT_ACTION_TEXT, 'actions.cancelReason');
  day(item.targetOn, 'actions.targetOn');
  const completed = day(item.completedOn, 'actions.completedOn');
  if (
    item.workOrderId !== null &&
    (typeof item.workOrderId !== 'string' || !UUID.test(item.workOrderId))
  ) {
    throw new DomainError('INVALID_DEFECT', 'actions.workOrderId');
  }
  if (item.status === 'completed') {
    if (blank(item.evidence) && item.workOrderId === null) {
      throw new DomainError('EVIDENCE_REQUIRED', item.id);
    }
    if (completed === null) throw new DomainError('COMPLETED_DATE_REQUIRED', item.id);
    if (compareLocalDate(completed, today) > 0) throw new DomainError('FUTURE_DATE', item.id);
  } else if (completed !== null) {
    throw new DomainError('INVALID_DEFECT', 'actions.completedOn');
  }
  if (item.status === 'cancelled' && blank(item.cancelReason)) {
    throw new DomainError('CANCEL_REASON_REQUIRED', item.id);
  }
}

/**
 * Kapanışın DM-17 koşulları (ADR 0060 §9): faaliyetlerin hepsi tamamlanmış ya da iptal
 * (`ACTIONS_OPEN`, iki modda). Yazma anında (`strict`) Part B (`CAPTAIN_ACTION_REQUIRED`), onarım
 * günü (`RECTIFIED_DATE_REQUIRED`) ve prosedür gözden geçirme cevabı (`PROCEDURE_REVIEW_REQUIRED`).
 */
export function assertDefectReportClosable(
  fields: DefectReportFields,
  defect: { immediateAction: string | null },
  context: { strict: boolean },
): void {
  if (fields.actions.some((a) => a.status !== 'completed' && a.status !== 'cancelled')) {
    throw new DomainError('ACTIONS_OPEN');
  }
  if (!context.strict) return;
  if (blank(defect.immediateAction)) throw new DomainError('CAPTAIN_ACTION_REQUIRED');
  if (fields.rectifiedOn === null) throw new DomainError('RECTIFIED_DATE_REQUIRED');
  if (fields.procedureReview === null) throw new DomainError('PROCEDURE_REVIEW_REQUIRED');
}

/**
 * Ana aşama (ADR 0096 §2): `open` → Reported; `investigating`, `repair`, `testing` → Under
 * Action; `verification` → Verification; kapanış onaylandı → Closed.
 */
export function defectStage(status: DefectStatus | string, closed: boolean): DefectStage {
  if (closed) return 'closed';
  switch (status) {
    case 'investigating':
    case 'repair':
    case 'testing':
      return 'under_action';
    case 'verification':
      return 'verification';
    default:
      return 'reported';
  }
}

/** Arızaya bağlı iş emrinin durumu (A7). */
export interface DefectWorkOrderState {
  /** İptal edilmemiş iş kaydı var. */
  reported: boolean;
  cancelled: boolean;
}

/**
 * Doğrulamaya kendiliğinden geçiş (ADR 0096 §7; ortak A7): arıza kapanmamış, kapanışı onay
 * beklemiyor, henüz doğrulamada değil ve iptal edilmemiş bağlı iş emirlerinin hepsi raporlanmış
 * (en az bir tane). İptal edilen iş emri gerekli sayılmaz.
 */
export function defectReadyForVerification(input: {
  status: DefectStatus | string;
  closed: boolean;
  pending: boolean;
  workOrders: readonly DefectWorkOrderState[];
}): boolean {
  if (input.closed || input.pending || input.status === 'verification') return false;
  const required = input.workOrders.filter((w) => !w.cancelled);
  return required.length > 0 && required.every((w) => w.reported);
}

/** Talebin parçası gelmiş ya da vazgeçilmiş sayıldığı durumlar (A6). */
const PARTS_SETTLED = new Set(['cancelled', 'closed']);

/**
 * "Under Action – Awaiting Parts" (ADR 0096 §8; ortak A6): arıza Under Action'da ve bağlı
 * taleplerden biri hâlâ açık (iptal edilmemiş, siparişi tamamlanmamış).
 */
export function defectAwaitingParts(
  stage: DefectStage,
  requisitionStatuses: readonly string[],
): boolean {
  return stage === 'under_action' && requisitionStatuses.some((s) => !PARTS_SETTLED.has(s));
}

/**
 * Geciken (ADR 0060 §5): kapanmamış arızanın hedef günü geçmiş ya da tamamlanmamış, iptal
 * edilmemiş bir faaliyetinin hedef günü geçmiş (gemi gününe göre).
 */
export function defectOverdue(
  fields: Pick<DefectReportFields, 'targetOn' | 'actions'>,
  context: { closed: boolean; today: LocalDate },
): boolean {
  if (context.closed) return false;
  const late = (target: LocalDate | null) =>
    target !== null && compareLocalDate(target, context.today) < 0;
  if (late(fields.targetOn)) return true;
  return fields.actions.some(
    (a) => a.status !== 'completed' && a.status !== 'cancelled' && late(a.targetOn),
  );
}
